import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUser } from '@fortawesome/free-solid-svg-icons';
import './SharedDetailModal.css';

const SharedDetailModal = ({ isOpen, onClose, title, data, config }) => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setIsVisible(true);
            document.body.style.overflow = 'hidden';
        } else {
            setTimeout(() => setIsVisible(false), 300); // Wait for transition
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen && !isVisible) return null;
    if (!data || !config) return null;

    const handleOverlayClick = (e) => {
        if (e.target.className === 'sdm-overlay sdm-visible' || e.target.className === 'sdm-overlay') {
            onClose();
        }
    };

    const renderValue = (field, dataRow) => {
        if (!dataRow) return 'N/A';
        if (typeof field.value === 'function') {
            try {
                return field.value(dataRow);
            } catch {
                return 'N/A';
            }
        }
        return dataRow[field.value] != null ? dataRow[field.value] : 'N/A';
    };

    // Extract basic profile details for the top card dynamically
    const displayName = data.name ||
        [data.first_name, data.last_name].filter(Boolean).join(' ').trim() ||
        data.username ||
        data.practice_name ||
        'Profile Details';
        
    const roleDisplay = data.role_name || (title ? title.replace(' Details', '') : '');
    
    // Attempt to extract a city/state location if available
    let locationDisplay = '';
    const city = data.address?.city || data.city;
    if (city) {
        locationDisplay = [city, data.address?.state_id || data.state].filter(Boolean).join(', ');
    }

    return (
        <div className={`sdm-overlay ${isOpen ? 'sdm-visible' : ''}`} onClick={handleOverlayClick}>
            <div className={`sdm-container ${isOpen ? 'sdm-animate-in' : 'sdm-animate-out'}`}>
                
                {/* Modal Header */}
                <div className="sdm-page-header">
                    <h2 className="sdm-page-title">{title || 'My Profile'}</h2>
                    <button className="sdm-close-btn" onClick={onClose} aria-label="Close">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="sdm-scroll-content">
                    {/* Top Avatar Card */}
                    <div className="sdm-profile-section">
                        <div className="sdm-avatar-circle">
                            <FontAwesomeIcon icon={faUser} className="sdm-avatar-icon" />
                        </div>
                        <div className="sdm-profile-info">
                            <h3 className="sdm-profile-name">{displayName}</h3>
                            {roleDisplay && <span className="sdm-profile-role">{roleDisplay}</span>}
                            {locationDisplay && <span className="sdm-profile-location">{locationDisplay}</span>}
                            {data.status && typeof data.status === 'string' && (
                                <span className={`sdm-profile-status sdm-status-${data.status.toLowerCase()}`}>
                                    {data.status.toUpperCase()}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Dynamic Configured Sections */}
                    {config.map((section, idx) => (
                        <div key={idx} className="sdm-info-section">
                            {section.title && (
                                <div className="sdm-section-header">
                                    <h3 className="sdm-section-title">{section.title}</h3>
                                </div>
                            )}

                            <div className="sdm-section-body">
                                <div className="sdm-info-grid">
                                    {section.fields.map((field, fIdx) => {
                                        const value = renderValue(field, data);
                                        return (
                                            <div key={fIdx} className={`sdm-field ${field.fullWidth ? 'sdm-full-width' : ''}`}>
                                                <span className="sdm-field-label">{field.label}</span>
                                                <div className={`sdm-field-value ${field.isBadge ? 'sdm-value-badge' : ''}`}>
                                                    {value}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default SharedDetailModal;
